import { Box } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { useEffect, useState } from "react";
import UploadSheetMapper from "./uploadTabSheetMapper";
import {
  getSavedOutputJson,
  setClearOutputJson,
} from "../../redux/slices/outputJson";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store/appStore";
import JsonEditor from "./jsonEditor";
import { clearRawJson, setRawJson } from "../../redux/slices/editor";
import { STATUS } from "../../utils/status";

export type MappingOptionType = string;
export type DataType = "int" | "number" | "string" | "array" | "object" | "";

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonObject
  | JsonValue[];
export interface JsonObject {
  [key: string]: JsonValue;
}

export interface Mapping {
  option: any;
  type: DataType;
}

interface JsonTabOptionsProps {
  jsonTab: number;
  onFormattedOutputChange?: (output: string) => void;
  parseError: string | null;
  setParseError: (value: string | null) => void;
}

export const JsonMapper: React.FC<JsonTabOptionsProps> = ({
  jsonTab,
  onFormattedOutputChange,
  parseError,
  setParseError,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const savedJsonSchema = useSelector(
    (state: RootState) => state.outputJson.data
  );
  const savedJsonSchemaStatus = useSelector(
    (state: RootState) => state.outputJson.getSavedOutputJsonStatus
  );
  const rawJson = useSelector((state: RootState) => state.editor.rawJson);
  const hasUserEdited = useSelector(
    (state: RootState) => state.editor.hasUserEdited
  );
  const [parsedJson, setParsedJson] = useState<JsonObject>({});
  const [keyMappings, setKeyMappings] = useState<Record<string, Mapping>>({});
  const [removeIfNull, setRemoveIfNull] = useState<Record<string, boolean>>({});
  const [removeIfBlank, setRemoveIfBlank] = useState<Record<string, boolean>>(
    {}
  );


  const [activeTab, setActiveTab] = useState(jsonTab);
  const [isParsing, setIsParsing] = useState(true);
  console.log({ keyMappings });
  useEffect(() => {
    setActiveTab(jsonTab);
  }, [jsonTab]);

  const handleSchemaApiRsponse = (resposneData: any) => {
    const formattedResponse = JSON.stringify(resposneData, null, 2)
    dispatch(setRawJson(formattedResponse));
  }

  const generateOutputJson = (
    obj: JsonValue,
    path: string[] = []
  ): JsonValue => {
    if (Array.isArray(obj)) {
      return obj.map((item, index) =>
        generateOutputJson(item, [...path, String(index)])
      );
    } else if (typeof obj === "object" && obj !== null) {
      const result: JsonObject = {};
      for (const key in obj) {
        const fullPath = [...path, key].join(".");
        const mapped = keyMappings[fullPath];
        const removeNull = removeIfNull[fullPath];
        const removeBlank = removeIfBlank[fullPath];

        if (mapped) {
          const mappingStr = `@${mapped.option}:${mapped.type}:${
            removeNull ? true : false
          }:${removeBlank ? true : false}`;
          result[key] = mappingStr; // 👈 mapping directly
        } else {
          result[key] = generateOutputJson(obj[key], [...path, key]);
        }
      }
      return result;
    } else {
      const fullPath = path.join(".");
      const mapped = keyMappings[fullPath];
      const removeNull = removeIfNull[fullPath];
      const removeBlank = removeIfBlank[fullPath];

      if (mapped) {
        const mappingStr = `@${mapped.option}:${mapped.type}:${
          removeNull ? true : false
        }:${removeBlank ? true : false}`;
        return mappingStr;
      }

      return obj;
    }
  };

  const formattedOutput = JSON.stringify(
    generateOutputJson(parsedJson),
    null,
    2
  );

  useEffect(() => {
    if (onFormattedOutputChange) onFormattedOutputChange(formattedOutput);
  }, [formattedOutput]);

  useEffect(() => {
    setIsParsing(true)
    try {
      const parsed = JSON.parse(rawJson);
      setParsedJson(parsed);
      setParseError(null);
    } catch (e) {
      setParseError(e instanceof Error ? e.message : "Invalid JSON");
      setParsedJson({});
    } finally {
      setIsParsing(false);
    }
  }, [rawJson]);

  const handleMappingChange = (keyPath: string, mapping: Mapping) => {
    setKeyMappings((prev) => ({ ...prev, [keyPath]: mapping }));
  };

  useEffect(() => {
    if (activeTab === 0) {
      dispatch(getSavedOutputJson({ project_id: id }));
    }
  }, [id, activeTab]);

  useEffect(() => {
    if (
      savedJsonSchemaStatus === STATUS.SUCESS &&
      savedJsonSchema?.data?.schema &&
      !hasUserEdited
    ) {
      try {
        //const parsedSchema = JSON.parse(savedJsonSchema.schema);
        const formattedJson = JSON.stringify(
          savedJsonSchema?.data?.schema,
          null,
          2
        );
        dispatch(setRawJson(formattedJson));
      } catch (err) {
        console.error("Failed to parse schema:", err);
      }
    }
  }, [savedJsonSchema, savedJsonSchemaStatus, hasUserEdited, dispatch, id]);

  const updateParsedJsonValue = (pathStr: string, newValue: string) => {
    const keys = pathStr.split(".");
    const updated = structuredClone(parsedJson);
    let current: any = updated;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      current[key] = current[key] ?? (isNaN(Number(keys[i + 1])) ? {} : []);
      current = current[key];
    }

    const finalKey = keys[keys.length - 1];
    try {
      const parsedVal =
        newValue === "null"
          ? null
          : newValue === "true"
          ? true
          : newValue === "false"
          ? false
          : isNaN(Number(newValue))
          ? newValue
          : Number(newValue);

      current[finalKey] = parsedVal;
      setParsedJson(updated);
      dispatch(setRawJson(JSON.stringify(updated, null, 2)));
    } catch (err) {
      console.warn("Failed to update value:", err);
    }
  };

  return (
    <div className="w-full">
      {activeTab === 0 && (
        <>
          <Box className="p-4">
            <JsonEditor
              value={rawJson}
              onChange={(val) => dispatch(setRawJson(val))}
              setParseError={setParseError}
              onApiResponse = {handleSchemaApiRsponse}
            />
          </Box>
        </>
      )}
      {activeTab === 1 && (
        <Box className="p-4">
          <div className="pr-1">
            {isParsing ? (
              <div className="text-center py-4 text-blue-600 font-medium">
                Parsing JSON...
              </div>
            ) : Object.keys(parsedJson).length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                No JSON data to map
              </div>
            ) : (
              <UploadSheetMapper
                parsedJson={parsedJson}
                keyMappings={keyMappings}
                handleMappingChange={handleMappingChange}
                updateParsedJsonValue={updateParsedJsonValue}
                removeIfNull={removeIfNull}
                removeIfBlank={removeIfBlank}
                setRemoveIfNull={setRemoveIfNull}
                setRemoveIfBlank={setRemoveIfBlank}
              />
            )}
          </div>
        </Box>
      )}
    </div>
  );
};
